import { h, type VNode } from 'vue'
import { Button, Modal } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import style from './base-confirm-modal.module.scss'

export type BaseConfirmContent = string | VNode

export type BaseConfirmOptions = {
  title: BaseConfirmContent
  content?: BaseConfirmContent
  okText?: BaseConfirmContent
  cancelText?: BaseConfirmContent
  onOk?: () => unknown | Promise<unknown>
  onCancel?: () => void
}

export type BaseConfirmInstance = ReturnType<typeof Modal.confirm>

export const useBaseConfirmModal = () => {
  const { t } = useI18n()

  const openConfirm = (options: BaseConfirmOptions): BaseConfirmInstance => {
    let isConfirming = false

    const closeConfirm = () => {
      confirmInstance.destroy()
    }

    const renderTitle = () => {
      return h('div', { class: `${style['modal-header']} modal-header-primary` }, [
        h('span', { class: style.confirm_modal_title }, options.title)
      ])
    }

    const renderContent = () => {
      return h('div', { class: style.confirm_action_container }, options.content ?? '')
    }

    const updatePendingState = () => {
      confirmInstance.update({
        keyboard: !isConfirming,
        footer: renderFooter()
      })
    }

    const onConfirm = async () => {
      if (isConfirming) return

      isConfirming = true
      updatePendingState()

      try {
        await options.onOk?.()
        closeConfirm()
      } catch {
        isConfirming = false
        updatePendingState()
      }
    }

    const onClose = () => {
      if (isConfirming) return

      closeConfirm()
      options.onCancel?.()
    }

    function renderFooter() {
      return h('div', { class: style.confirm_button_container }, [
        h(
          Button,
          {
            type: 'primary',
            class: [style.confirm_button, 'confirm-btn'],
            loading: isConfirming,
            disabled: isConfirming,
            onClick: onConfirm
          },
          () => options.okText ?? t('Common.Button.Confirm')
        ),
        h(
          Button,
          {
            class: ['ant-btn-outlined', style.confirm_button],
            disabled: isConfirming,
            onClick: onClose
          },
          () => options.cancelText ?? t('Common.Button.Cancel')
        )
      ])
    }

    const confirmInstance = Modal.confirm({
      class: style.confirm_modal,
      icon: null,
      okCancel: false,
      closable: false,
      maskClosable: false,
      keyboard: true,
      title: renderTitle(),
      content: renderContent(),
      footer: renderFooter(),
      onCancel: onClose
    })

    return confirmInstance
  }

  return {
    openConfirm
  }
}
